import React, { useContext, useCallback, Fragment, useState } from 'react';
import './TimeAdjustment.css';
import Comment from '../Comment/Comment';
import CbmContext from '../../Context/Cbm/cbmContext';
import EmployeeInfo from '../EmployeeInfo/EmployeeInfo';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';

const TimeAdjustment = ({ setPayLoad, setFormError, errMsg }) => {
  const cbmContext = useContext(CbmContext);
  const { portalUser, submittedData, errorCode } = cbmContext;

  const [timeAdjustmentData, setTimeAdjustmentData] = useState({
    employee: {
      employeeNumber: '',
      employeeFirst: '',
      employeeMiddle: '',
      employeeLast: '',
      employeeSecondLast: '',
    },
    breaks: [
      {
        date: '',
        punchIn: '',
        punchOut: '',
        breakAmount: '',
      },
    ],
    reasonMissed: '',
    managerSig: null,
    employeeSig: null,
    portalUser,
  });

  // Use unified form setup hook
  const {
    validationErrors,
    getArrayFieldError,
    hasArrayFieldError,
    clearArrayItemErrors,
    getFieldError,
  } = useFormSetup({
    formName: 'timeAdjustment',
    formData: timeAdjustmentData,
    setFormData: setTimeAdjustmentData,
    setPayLoad,
    setFormError,
    submittedData,
    errorCode,
  });

  const updateAdjustment = useCallback(
    (e, index) => {
      const { name, value } = e.target;
      setTimeAdjustmentData((prev) => {
        const adjustmentTemp = [...prev.breaks];
        adjustmentTemp[index][name] = value;
        return { ...prev, breaks: adjustmentTemp };
      });
    },
    [setTimeAdjustmentData]
  );

  const addAdjustment = useCallback(() => {
    setTimeAdjustmentData((prev) => ({
      ...prev,
      breaks: [
        ...prev.breaks,
        { date: '', punchIn: '', punchOut: '', breakAmount: '' },
      ],
    }));
  }, [setTimeAdjustmentData]);

  const removeAdjustment = useCallback(
    (index) => {
      clearArrayItemErrors('breaks', index);
      setTimeAdjustmentData((prev) => ({
        ...prev,
        breaks: prev.breaks.filter((_, i) => i !== index),
      }));
    },
    [clearArrayItemErrors, setTimeAdjustmentData]
  );

  const changeEmpInfo = useCallback(
    (e) => {
      const { name, value } = e.target;
      setTimeAdjustmentData((prev) => ({
        ...prev,
        employee: { ...prev.employee, [name]: value },
      }));
    },
    [setTimeAdjustmentData]
  );

  const setReasonMissed = useCallback(
    (e) => {
      const { value } = e.target;
      setTimeAdjustmentData((prev) => ({ ...prev, reasonMissed: value }));
    },
    [setTimeAdjustmentData]
  );
  return (
    <Fragment>
      <div className="heading">
        <h1>Time Adjustment</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      <fieldset>
        <legend>Employee Info</legend>
        <EmployeeInfo
          payload={timeAdjustmentData}
          changeEmpInfo={changeEmpInfo}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
      <fieldset className="timeAdjustmentInfo">
        <legend>Time adjustment info</legend>
        {timeAdjustmentData.breaks.map((adjustment, index) => {
          return (
            <div key={'break' + index} className="timeAdjustment">
              <div
                className={
                  errMsg && hasArrayFieldError('breaks', index, 'date')
                    ? 'adjustmentItem error'
                    : 'adjustmentItem'
                }
              >
                <label htmlFor="date" className="dateLbl required">
                  {'Date ' + (index + 1)}
                </label>
                <input
                  type="date"
                  id="date"
                  name={'date'}
                  onChange={(e) => updateAdjustment(e, index)}
                  value={adjustment.date || ''}
                  className="dateItem"
                />
                {errMsg && getArrayFieldError('breaks', index, 'date') && (
                  <span className="error-text">
                    {getArrayFieldError('breaks', index, 'date')}
                  </span>
                )}
              </div>
              <div
                className={
                  errMsg && hasArrayFieldError('breaks', index, 'punchIn')
                    ? 'adjustmentItem error'
                    : 'adjustmentItem'
                }
              >
                <label htmlFor="in" className="dateLbl required">
                  {'In ' + (index + 1)}
                </label>
                <input
                  type="datetime-local"
                  id="in"
                  name="punchIn"
                  onChange={(e) => updateAdjustment(e, index)}
                  value={adjustment.punchIn ?? ''}
                  className="dateItem"
                />
                {errMsg && getArrayFieldError('breaks', index, 'punchIn') && (
                  <span className="error-text">
                    {getArrayFieldError('breaks', index, 'punchIn')}
                  </span>
                )}
              </div>
              <div
                className={
                  errMsg && hasArrayFieldError('breaks', index, 'punchOut')
                    ? 'adjustmentItem error'
                    : 'adjustmentItem'
                }
              >
                <label htmlFor="out" className="dateLbl required">
                  {'Out ' + (index + 1)}
                </label>
                <input
                  type="datetime-local"
                  id="out"
                  name="punchOut"
                  onChange={(e) => updateAdjustment(e, index)}
                  value={adjustment.punchOut ?? ''}
                  className="dateItem"
                />
                {errMsg && getArrayFieldError('breaks', index, 'punchOut') && (
                  <span className="error-text">
                    {getArrayFieldError('breaks', index, 'punchOut')}
                  </span>
                )}
              </div>
              <div className="adjustmentItem">
                <label htmlFor="breakTime" className="breakTimeLbl">
                  {'Break ' + (index + 1)}
                </label>
                <input
                  type="number"
                  id="breakTime"
                  name="breakAmount"
                  onChange={(e) => updateAdjustment(e, index)}
                  value={adjustment.breakAmount || ''}
                  className="dateItem"
                />
              </div>
              <div className="timeAdjust-add-remove-btns">
                <button className="add" onClick={() => addAdjustment()}>
                  Add entry
                </button>
                {timeAdjustmentData.breaks.length > 1 && (
                  <button
                    className="remove"
                    onClick={() => removeAdjustment(index)}
                  >
                    Remove entry
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </fieldset>
      <fieldset className="commentInfo">
        <legend>Comments</legend>
        <div className="comments">
          <Comment
            commentsId={'reasonPayMissedId'}
            payload={timeAdjustmentData.reasonMissed}
            onHandleChange={setReasonMissed}
            getFieldError={getFieldError}
            errMsg={errMsg}
          />
        </div>
      </fieldset>
    </Fragment>
  );
};
// eslint-disable-next-line
TimeAdjustment.propTypes = {};

export default TimeAdjustment;
